import { useState } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import BreadcrumbTooltipItem from './BreadcrumbTooltipItem.tsx';
import NavigationBackButton from './NavigationBackButton.tsx';

export default function VariableBreadcrumbs({ breadcrumbs, selectNode, isEditor }) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const isMenuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const handleNavigate = (crumb) => {
    selectNode(crumb);
    handleMenuClose();
  };

  const hasManyCrumbs = breadcrumbs.length > 2;
  const startCrumbs = hasManyCrumbs ? breadcrumbs.slice(0, 1) : breadcrumbs.slice(0, -1);
  const middleCrumbs = hasManyCrumbs ? breadcrumbs.slice(1, -1) : [];
  const lastCrumb = breadcrumbs.at(-1);
  const parentCrumb = breadcrumbs.at(-2);
  const middleMenuStyle = isEditor
    ? {
        sx: {
          '.MuiPaper-root.MuiMenu-paper': {
            top: '76px !important',
            left: '86px !important',
          },
        },
      }
    : {};

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      sx={{ mt: '5px', padding: isEditor ? '0 16px' : '0' }}
    >
      {lastCrumb && parentCrumb && <NavigationBackButton onBackClick={() => selectNode(parentCrumb)} />}
      <Breadcrumbs
        data-testid={'breadcrumbs'}
        aria-label="breadcrumbs"
        sx={{
          lineHeight: '30px',
          width: isEditor ? '100%' : 'calc(100% - 35px)',
          '& .MuiBreadcrumbs-ol': {
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: '4px',
          },
        }}
      >
        {startCrumbs.map((crumb) => (
          <Link key={crumb.path} sx={{ cursor: 'pointer', color: 'rgba(0, 40, 58, 0.6)' }} onClick={() => selectNode(crumb)}>
            {crumb.friendlyName}
          </Link>
        ))}

        {middleCrumbs.length > 0 && (
          <span>
            <IconButton
              size="small"
              color="primary"
              data-testid={'middle-crumbs-menu-button'}
              onClick={handleMenuOpen}
              sx={{ padding: '0 5px', backgroundColor: '#F5F5F5', borderRadius: '2px' }}
            >
              <MoreHorizIcon sx={{ width: '16px', height: '16px' }} />
            </IconButton>

            <Menu
              slotProps={{
                root: {
                  disablePortal: true,
                  ...middleMenuStyle,
                },
              }}
              anchorEl={menuAnchor}
              open={isMenuOpen}
              onClose={handleMenuClose}
              aria-labelledby="breadcrumbs-menu"
            >
              {middleCrumbs.map((crumb) => (
                <MenuItem key={crumb.path} onClick={() => handleNavigate(crumb)}>
                  {crumb.friendlyName}
                </MenuItem>
              ))}
            </Menu>
          </span>
        )}

        {lastCrumb && <BreadcrumbTooltipItem isMiddleActive={middleCrumbs.length > 0} title={lastCrumb.friendlyName} />}
      </Breadcrumbs>
    </Box>
  );
}
